// API client with automatic access-token injection and one-shot 401 refresh.
// Mirrors /app/mobile/src/lib/api.ts exactly.
import { KEYS, storage } from './storage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export class ApiError extends Error {
  constructor(err, status) {
    super(err.message || 'Request failed');
    this.code = err.code || 'UNKNOWN';
    this.status = status;
    this.requestId = err.requestId;
    this.details = err.details;
  }
}

let refreshInFlight = null;

async function performRefresh() {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const refresh = storage.get(KEYS.refreshToken);
    if (!refresh) return false;
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (!res.ok) {
        storage.remove(KEYS.accessToken);
        storage.remove(KEYS.refreshToken);
        return false;
      }
      const body = await res.json();
      if (body.access_token) storage.set(KEYS.accessToken, body.access_token);
      if (body.refresh_token) storage.set(KEYS.refreshToken, body.refresh_token);
      return true;
    } catch {
      return false;
    }
  })();
  const ok = await refreshInFlight;
  refreshInFlight = null;
  return ok;
}

export async function apiFetch(path, init = {}) {
  const url = path.startsWith('http') ? path : `${BACKEND_URL}${path}`;
  const auth = init.auth !== false;
  const headers = {
    'Content-Type': 'application/json',
    ...(init.headers || {}),
  };
  if (auth) {
    const at = storage.get(KEYS.accessToken);
    if (at) headers['Authorization'] = `Bearer ${at}`;
  }

  let res = await fetch(url, { ...init, headers });

  // If 401 and we have a refresh token, try one-shot refresh + retry
  if (
    res.status === 401 &&
    auth &&
    !init.skipRefresh &&
    !path.includes('/auth/refresh') &&
    !path.includes('/auth/sign-in')
  ) {
    const refreshed = await performRefresh();
    if (refreshed) {
      const at2 = storage.get(KEYS.accessToken);
      if (at2) headers['Authorization'] = `Bearer ${at2}`;
      res = await fetch(url, { ...init, headers });
    }
  }

  const text = await res.text();
  let body = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = text; }
  if (!res.ok) {
    const err = body && body.error ? body.error : { code: 'HTTP_ERROR', message: `HTTP ${res.status}` };
    throw new ApiError(err, res.status);
  }
  return body;
}

/** XHR-based upload with progress callback. */
export function uploadWithProgress(url, formField, file, onProgress, authToken) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const fullUrl = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
    xhr.open('POST', fullUrl);
    if (authToken) xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.min(1, e.loaded / e.total));
    };
    xhr.onload = () => {
      let parsed = null;
      try { parsed = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch { parsed = xhr.responseText; }
      if (xhr.status >= 200 && xhr.status < 300) resolve(parsed);
      else {
        const err = parsed && parsed.error ? parsed.error : { code: 'HTTP_ERROR', message: `HTTP ${xhr.status}` };
        reject(new ApiError(err, xhr.status));
      }
    };
    xhr.onerror = () => reject(new ApiError({ code: 'NETWORK_ERROR', message: 'Network request failed' }, 0));
    xhr.onabort = () => reject(new ApiError({ code: 'ABORTED', message: 'Upload aborted' }, 0));
    const fd = new FormData();
    fd.append(formField, file);
    xhr.send(fd);
  });
}

export { BACKEND_URL };
